import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon,
  UserPlusIcon,
  KeyIcon,
  ClockIcon,
  EyeIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

import { API_BASE } from '../../lib/config';

const SecurityManagement = ({ onBack }) => {
  const [admins, setAdmins] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    admin_id: '',
    password: '',
    name: '',
    role: 'カスタマーサポート',
    permissions: []
  });

  useEffect(() => {
    fetchAdmins();
    fetchAccessLogs();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Note: UserManagement uses 'admin_token', others 'token'. 
      // Assuming 'token' is the standard now based on recent Auth context use.
      // Fallback to 'admin_token' if 'token' is missing might be safer if mixed usage exists.
      const authToken = token || localStorage.getItem('admin_token');

      const res = await fetch(`${API_BASE}/admin/users?type=admin`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (res.ok) {
        const data = await res.json();
        const mapped = data.map(u => ({
          id: u.id,
          admin_id: u.email, // using email as admin_id for display
          name: u.name,
          role: u.user_type === 'admin' ? 'システム管理者' : '運営スタッフ', // simple mapping
          permissions: ['all'], // Default for now
          last_login: u.last_active_at,
          created_at: u.created_at,
          status: u.status || 'active',
          two_factor_enabled: false
        }));
        setAdmins(mapped);
      } else {
        // Silently fail or set empty for now to avoid error toast loop on auth fail
        setAdmins([]);
      }
    } catch (e) {
      console.error(e);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessLogs = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/access-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccessLogs(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.admin_id || !newAdmin.password || !newAdmin.name) {
      toast.error('必須項目を入力してください');
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newAdmin)
      });

      if (res.ok) {
        toast.success('管理者を作成しました');
        setShowCreateModal(false);
        fetchAdmins();
        setNewAdmin({ ...newAdmin, admin_id: '', password: '', name: '' });
      } else {
        const err = await res.json();
        toast.error(err.detail || '作成に失敗しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('エラーが発生しました');
    }
  };

  const handleToggleAdminStatus = async (adminId) => {
    const admin = admins.find(a => a.id === adminId);
    if (!admin) return;

    if (!window.confirm(`${admin.name} のステータスを変更しますか？`)) return;

    const newStatus = admin.status === 'active' ? 'suspended' : 'active'; // Backend uses 'suspended' not 'inactive' usually? AdminView model says 'active', 'suspended'.

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/users/${adminId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success('ステータスを更新しました');
        setAdmins(prev => prev.map(a => a.id === adminId ? { ...a, status: newStatus } : a));
      } else {
        const err = await res.json();
        toast.error(err.detail || '更新に失敗しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('エラーが発生しました');
    }
  };

  const handleToggle2FA = (adminId) => {
    toast.error('二段階認証設定は現在サポートされていません');
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'bg-green-100 text-green-800', label: 'アクティブ' },
      suspended: { class: 'bg-red-100 text-red-800', label: '停止中' },
      inactive: { class: 'bg-gray-100 text-gray-800', label: '無効' }
    };
    const badge = badges[status] || badges.active;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getLogStatusIcon = (status) => {
    // Access logs mock data removed, simple placeholder
    if (status === 'success') {
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
    return <XMarkIcon className="w-4 h-4 text-red-500" />;
  };

  const availablePermissions = [
    { id: 'all', label: '全権限' },
    { id: 'users', label: 'ユーザー管理' },
    { id: 'jobs', label: '求人管理' },
    { id: 'payments', label: '決済管理' },
    { id: 'reports', label: '通報対応' },
    { id: 'notifications', label: '通知管理' },
    { id: 'settings', label: 'システム設定' },
    { id: 'analytics', label: 'アナリティクス' },
    { id: 'admin_management', label: '管理者管理' }
  ];

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">セキュリティ・アクセス管理</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <UserPlusIcon className="w-4 h-4 mr-2" />
          管理者追加
        </button>
      </div>

      {/* セキュリティ状況 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <ShieldCheckIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">アクティブ管理者</p>
              <p className="text-2xl font-bold text-gray-900">
                {admins.filter(admin => admin.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <LockClosedIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">2FA有効</p>
              <p className="text-2xl font-bold text-gray-900">
                {admins.filter(admin => admin.two_factor_enabled).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">ログイン失敗</p>
              <p className="text-2xl font-bold text-gray-900">
                {accessLogs.filter(log => log.status === 'failed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">総ログ数</p>
              <p className="text-2xl font-bold text-gray-900">{accessLogs.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 管理者アカウント */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">管理者アカウント</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    管理者情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    権限
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                        <div className="text-sm text-gray-500">{admin.admin_id}</div>
                        <div className="text-sm text-gray-500">{admin.role}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {admin.permissions.map((perm, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {perm === 'all' ? '全権限' : perm}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getStatusBadge(admin.status)}
                        <div className="flex items-center text-xs">
                          {admin.two_factor_enabled ? (
                            <><LockClosedIcon className="w-3 h-3 text-green-500 mr-1" /> 2FA有効</>
                          ) : (
                            <><KeyIcon className="w-3 h-3 text-gray-400 mr-1" /> 2FA無効</>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="space-y-1">
                        <button
                          onClick={() => handleToggleAdminStatus(admin.id)}
                          className={`text-xs px-2 py-1 rounded ${admin.status === 'active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                        >
                          {admin.status === 'active' ? '無効化' : '有効化'}
                        </button>
                        <button
                          onClick={() => handleToggle2FA(admin.id)}
                          className="block text-xs px-2 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded"
                        >
                          2FA {admin.two_factor_enabled ? '無効' : '有効'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* アクセスログ */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">アクセスログ</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-2 p-6">
              {accessLogs.map((log) => (
                <div key={log.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    {getLogStatusIcon(log.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        {log.admin_name} - {log.action}
                      </p>
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString('ja-JP')}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>IP: {log.ip_address}</div>
                      <div>UA: {log.user_agent}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 管理者作成モーダル */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">新規管理者作成</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateAdmin} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    管理者ID
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newAdmin.admin_id}
                    onChange={(e) => setNewAdmin({ ...newAdmin, admin_id: e.target.value })}
                    placeholder="admin123"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    パスワード
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    placeholder="********"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    名前
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newAdmin.name}
                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                    placeholder="管理者名"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    役職
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newAdmin.role}
                    onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                  >
                    <option value="カスタマーサポート">カスタマーサポート</option>
                    <option value="モデレーター">モデレーター</option>
                    <option value="システム管理者">システム管理者</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  権限
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {availablePermissions.map((perm) => (
                    <label key={perm.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newAdmin.permissions.includes(perm.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewAdmin({
                              ...newAdmin,
                              permissions: [...newAdmin.permissions, perm.id]
                            });
                          } else {
                            setNewAdmin({
                              ...newAdmin,
                              permissions: newAdmin.permissions.filter(p => p !== perm.id)
                            });
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <UserPlusIcon className="w-4 h-4 mr-2" />
                  作成
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityManagement;